import type { ApolloClient } from '@apollo/client'
import { ConversationsDocument, type ConversationsQueryVariables } from '@gql'

/**
 * The single owner of "refresh the conversation list from the network".
 *
 * Two independent listeners sit on the same per-user `inboxChanged` stream —
 * the workspace (`useLiveMessaging`, to reorder rows) and the app-wide unread
 * feed (`useGlobalUnreadFeed`, to recount the nav badge) — and each used to
 * issue its own read on its own timer. One inbound message therefore cost two
 * identical `Conversations` round trips, plus a third whenever opening the
 * thread advanced the read watermark and re-triggered the badge. Apollo's
 * request deduplication could not help: the timers were 1.5s and 400ms, so the
 * reads never overlapped.
 *
 * Routing both through here collapses a nudge burst into ONE read. Callers
 * that need specific variables fresh even when nothing on screen watches them
 * (the badge, which must stay live outside the messaging section) pass
 * `ensure`; a run that did not already cover those variables tops up with a
 * direct read for them alone.
 */

/** Coalescing window, matching the burst rate of delivery-state ticks. */
const REFRESH_WINDOW_MS = 1_500

/**
 * How long after a run starts a new request is treated as reacting to the SAME
 * ws frame and joins it, rather than booking a trailing run. The two listeners
 * fire in the same tick, so this only has to cover a tick; a genuinely later
 * change books its own run as before, which is what keeps "after a nudge, the
 * list is fresh" true.
 */
const SAME_FRAME_MS = 250

interface RefreshState {
  lastAt: number
  timer: ReturnType<typeof setTimeout> | undefined
  inFlight: Promise<void> | null
  /** Variable sets owed a guaranteed-fresh read, keyed by `variablesKey`. */
  pendingEnsure: Map<string, ConversationsQueryVariables>
  /** Callers riding the booked trailing run. */
  waiters: Array<() => void>
}

const state: RefreshState = {
  inFlight: null,
  lastAt: 0,
  pendingEnsure: new Map(),
  timer: undefined,
  waiters: [],
}

/**
 * Order-insensitive identity for a variables object (one level is enough).
 *
 * Two kinds of noise have to be normalised away, both of which silently broke
 * the match when this was a plain deep-equal:
 *
 *  - `undefined` vs an absent key. The workspace omits `operatorId` for the
 *    operator's own inbox; the badge passes it as `undefined`. Same read.
 *  - The app's `useQuery` wrapper injects a `PERMISSION_*` boolean per
 *    permission into EVERY query's variables (they drive `@include`
 *    directives, and are stripped before the request). A mounted query
 *    therefore carries a dozen keys the caller never wrote, so an exact
 *    comparison against hand-written variables can never match.
 */
function variablesKey(variables: object | undefined): string {
  if (!variables) {
    return ''
  }

  const entries = variables as Record<string, unknown>
  return JSON.stringify(
    Object.keys(entries)
      .sort()
      .filter((key) => entries[key] !== undefined && !key.startsWith('PERMISSION_'))
      .map((key) => [key, entries[key]]),
  )
}

async function run(client: ApolloClient): Promise<void> {
  state.lastAt = Date.now()

  // Refetch whatever list is actually on screen — the workspace inbox, the
  // triage queue, a peer's list — under its own variables.
  const { queries, results } = client.refetchQueries({ include: [ConversationsDocument] })
  const covered = new Set(queries.map((query) => variablesKey(query.variables)))
  await Promise.all(results.map((result) => Promise.resolve(result).catch(() => undefined)))

  // The owed set is taken HERE, not at the top: a caller that joined this run
  // mid-flight has already registered its variables by now, so joining never
  // costs it its guarantee.
  const owed = state.pendingEnsure
  state.pendingEnsure = new Map()

  // Anything owed that no on-screen query just refreshed (the badge while the
  // operator is anywhere else in the app, or while they are looking at the
  // Unassigned queue rather than their own inbox).
  await Promise.all(
    [...owed.entries()]
      .filter(([key]) => !covered.has(key))
      .map(([, variables]) =>
        client
          .query({
            fetchPolicy: 'network-only',
            query: ConversationsDocument,
            variables,
          })
          .catch(() => undefined),
      ),
  )
}

function schedule(client: ApolloClient): Promise<void> {
  if (state.inFlight) {
    return state.inFlight
  }

  const pending = run(client).finally(() => {
    state.inFlight = null
  })
  state.inFlight = pending
  return pending
}

export interface InboxRefreshOptions {
  /**
   * Variables that must be freshly read even if no mounted query uses them.
   * Omit when the caller only cares about the lists already on screen.
   */
  ensure?: ConversationsQueryVariables
  /**
   * Skip the coalescing window. For state changes the operator is waiting on
   * (losing access to the open thread, a row the push could not patch,
   * reconnect) rather than the steady stream of message nudges.
   */
  immediate?: boolean
}

/**
 * Requests a conversation-list refresh, coalesced with every other request in
 * the same window. Resolves once the read this request was folded into has
 * settled, so a caller can read the result straight from the cache afterwards.
 */
export function requestInboxRefresh(
  client: ApolloClient,
  { ensure, immediate = false }: InboxRefreshOptions = {},
): Promise<void> {
  if (ensure) {
    state.pendingEnsure.set(variablesKey(ensure), ensure)
  }

  const elapsed = Date.now() - state.lastAt

  // Same-frame join: the other listener already started this exact read.
  if (state.inFlight && elapsed <= SAME_FRAME_MS) {
    return state.inFlight
  }

  if (immediate || elapsed >= REFRESH_WINDOW_MS) {
    if (state.timer) {
      clearTimeout(state.timer)
      state.timer = undefined
    }

    return schedule(client)
  }

  // Inside the window: ride the trailing run — booking one if this is the
  // first request of the burst, else joining the one already booked.
  if (!state.timer) {
    state.timer = setTimeout(() => {
      state.timer = undefined
      const waiting = state.waiters.splice(0)
      void schedule(client).finally(() => waiting.forEach((resolve) => resolve()))
    }, REFRESH_WINDOW_MS - elapsed)
  }

  return new Promise<void>((resolve) => state.waiters.push(resolve))
}

/**
 * Cancels a booked trailing run and releases anyone waiting on it. For
 * teardown — a waiter left pending would strand its caller's `await`.
 */
export function cancelInboxRefresh(): void {
  if (state.timer) {
    clearTimeout(state.timer)
    state.timer = undefined
  }

  state.waiters.splice(0).forEach((resolve) => resolve())
}

/** Test seam — drops all scheduler state. */
export function resetInboxRefreshForTest(): void {
  cancelInboxRefresh()
  state.inFlight = null
  state.lastAt = 0
  state.pendingEnsure = new Map()
  state.waiters = []
}
